/**
 * The OFD measurement-unit classifiers (`unit_code`) a merchant can pick from.
 *
 * The wire field is a free-form string and the backend assigns no vocabulary of
 * its own, so this list IS the contract on the client side: the merchant picks
 * a unit by name and the stored value is its Soliq tasnif number. Codes are
 * kept as strings, not numbers, because that is what the API stores and
 * returns — a numeric `1` would round-trip as `"1"` anyway.
 *
 * Labels live in i18n (`menu.product.form.units.*`), not here, so all three
 * locales stay together; this list owns only the codes and their order.
 */
export const UNIT_CODES = [
  { code: '1', labelKey: 'kg' },
  { code: '2', labelKey: 'g' },
  { code: '3', labelKey: 'l' },
  { code: '4', labelKey: 'ml' },
  { code: '5', labelKey: 'pcs' },
  { code: '6', labelKey: 'portion' },
] as const;

export const MEASUREMENT_TYPES = [
  { code: 'MEASUREMENT_TYPE_GR', labelKey: 'g' },
  { code: 'MEASUREMENT_TYPE_KG', labelKey: 'kg' },
  { code: 'MEASUREMENT_TYPE_ML', labelKey: 'ml' },
  { code: 'MEASUREMENT_TYPE_L', labelKey: 'l' },
  { code: 'MEASUREMENT_TYPE_PCS', labelKey: 'pcs' },
  { code: 'MEASUREMENT_TYPE_PORTION', labelKey: 'portion' },
] as const;

/** A stored `unit_code` value, e.g. `'1'` for kilograms. */
export type UnitCode = (typeof UNIT_CODES)[number]['code'];
export type MeasurementTypeCode = (typeof MEASUREMENT_TYPES)[number]['code'];

/** The i18n key (under `menu.product.form.units`) naming a fiscal `unit_code`. */
export function unitLabelKey(code: string | undefined): string | undefined {
  return UNIT_CODES.find((unit) => unit.code === code)?.labelKey;
}

/** The i18n key (under `menu.product.form.units`) naming `measurement_type`. */
export function measurementTypeLabelKey(code: string | undefined): string | undefined {
  return MEASUREMENT_TYPES.find((unit) => unit.code === code)?.labelKey;
}

/** kg and l are the only units a merchant measures in fractions. */
export function unitAllowsDecimals(code: string | undefined): boolean {
  return code === '1' || code === '3';
}

/** Every valid code, for validating a seeded value against the select. */
const KNOWN_CODES = new Set<string>(UNIT_CODES.map((unit) => unit.code));

/**
 * Whether a stored `unit_code` is one this select can display.
 *
 * A product may carry a code assigned before this list existed (or one the tax
 * authority adds later). The select cannot show such a value — it would render
 * blank and silently rewrite the code on the next save — so callers drop it and
 * leave the field empty rather than pretending to represent it.
 */
export function isKnownUnitCode(code: string | undefined): code is UnitCode {
  return code !== undefined && KNOWN_CODES.has(code);
}
